import React from "react";
import { Button } from "@/components/ui/button";

/**
 * Pagination réutilisable côté frontend.
 *
 * Props:
 * - page (1-indexed)
 * - pageSize
 * - total
 * - onPageChange(newPage)
 * - onPageSizeChange(newSize)  // optionnel — si présent, affiche le sélecteur
 * - pageSizeOptions (défaut [10,20,50])
 * - testIdPrefix (préfixe pour data-testid, défaut "pagination")
 */
export default function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
  testIdPrefix = "pagination",
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const canPrev = safePage > 1;
  const canNext = safePage < totalPages;

  if (total === 0) return null;

  return (
    <div
      data-testid={`${testIdPrefix}-bar`}
      className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-slate-300"
    >
      <div data-testid={`${testIdPrefix}-count`} className="flex items-center gap-3">
        <span className="text-slate-200">
          {total} {total > 1 ? "résultats" : "résultat"}
        </span>
        {onPageSizeChange && (
          <label className="flex items-center gap-1 text-slate-300">
            <span className="hidden sm:inline">Par page :</span>
            <select
              data-testid={`${testIdPrefix}-page-size`}
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="rounded-md border border-white/10 bg-white/[0.08] px-2 py-1 text-xs text-slate-100 focus:outline-none"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          data-testid={`${testIdPrefix}-prev`}
          variant="outline"
          size="sm"
          disabled={!canPrev}
          onClick={() => canPrev && onPageChange(safePage - 1)}
          className="h-7 border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10 disabled:opacity-40"
        >
          ← Précédent
        </Button>
        <span data-testid={`${testIdPrefix}-info`} className="px-2 text-slate-200">
          Page {safePage} / {totalPages}
        </span>
        <Button
          data-testid={`${testIdPrefix}-next`}
          variant="outline"
          size="sm"
          disabled={!canNext}
          onClick={() => canNext && onPageChange(safePage + 1)}
          className="h-7 border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10 disabled:opacity-40"
        >
          Suivant →
        </Button>
      </div>
    </div>
  );
}

/**
 * Hook utilitaire — retourne {page, setPage, pageSize, setPageSize, paginated, total}
 * Réinitialise automatiquement à page 1 quand `resetKey` change (ex: hash des filtres).
 */
export function usePagination(items, { defaultPageSize = 20, resetKey } = {}) {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);

  // Reset à la page 1 quand les filtres changent
  React.useEffect(() => {
    setPage(1);
  }, [resetKey]);

  const total = items?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const paginated = (items || []).slice(start, start + pageSize);

  const handleSetPageSize = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  return {
    page: safePage,
    setPage,
    pageSize,
    setPageSize: handleSetPageSize,
    paginated,
    total,
  };
}
